/**
 * Abas do Painel de Assistências v2. Cada aba recebe o payload inteiro de
 * GET /api/solicitacoes/painel-assistencias e lê UM bloco:
 *   volume · backlog · prazos · qualidade · custos · reincidencia · pessoas ·
 *   lotes + dados.
 * `onDrill(conjunto, titulo)` abre a lista; `onFiltrar(dimensao, valor,
 * label)` aplica um filtro a partir de um clique no gráfico.
 *
 * Chaves conferidas contra solicitacoes/painel.py — chave errada em JS não
 * dá erro, dá "—" na tela.
 */
import React from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  ESTADO_PRAZO_TONE, SITUACAO_LABELS, SITUACAO_TONES, TIPOS_SOLICITACAO,
} from "../../../services/solicitacoes";
import { VazioInline } from "../_shared/Estados";
import {
  BarrasTom, Card, Kpi, KpiStrip, Pareto, Percentis, Regua, TOOLTIP_TEMA, TabelaSimples,
} from "./_blocos";
import { CalendarioHeatmap, HorariosPico, MotivosDonut } from "./_widgets";
import { du, inteiro, pct, reais } from "./recorte";

// Rótulos de tipo e situação saem do front (acentuados); os do backend são
// constantes legadas sem acento.
const TIPO_LABEL = Object.fromEntries(TIPOS_SOLICITACAO.map((t) => [t.value, t.label]));

const MESES_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const DIAS_SEMANA =["", "seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

function rotuloMes(iso) {
  const [ano, mes] = String(iso || "").split("-");
  const i = Number(mes) - 1;
  return i >= 0 && i < 12 ? `${MESES_PT[i]}/${String(ano).slice(2)}` : iso;
}

// ------------------------------------------------------------------ Visão geral

function SerieMensal({ serie }) {
  const data = (serie || []).map((m) => ({ ...m, mes: rotuloMes(m.mes) }));
  if (data.every((m) => !m.emitidas && !m.atendidas)) return <VazioInline>Sem movimento no período</VazioInline>;
  return (
    <ResponsiveContainer width="100%" height={210}>
      <AreaChart data={data} margin={{ top: 6, right: 6, left: -4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" />
        <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "var(--ink-3)" }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={36} />
        <Tooltip {...TOOLTIP_TEMA} />
        <Area type="monotone" dataKey="emitidas" name="Emitidas" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.18} strokeWidth={2} />
        <Area type="monotone" dataKey="atendidas" name="Atendidas (por data de atendimento)" stroke="var(--success-solid)" fill="var(--success-solid)" fillOpacity={0.12} strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DiaSemana({ dias }) {
  const data = (dias || []).map((d) => ({ dia: DIAS_SEMANA[d.dia] || d.dia, total: d.total }));
  if (data.every((d) => !d.total)) return <VazioInline>Sem registros no período</VazioInline>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 6, right: 6, left: -4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" vertical={false} />
        <XAxis dataKey="dia" tick={{ fontSize: 10, fill: "var(--ink-3)" }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={36} />
        <Tooltip {...TOOLTIP_TEMA} formatter={(v) => [`${v} solicitação(ões)`, "Total"]} />
        <Bar dataKey="total" radius={[3, 3, 0, 0]} fill="var(--chart-2)" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function AbaVisaoGeral({ dados, onDrill, onFiltrar }) {
  const v = dados.volume;
  return (
    <>
      <KpiStrip>
        <Kpi rotulo="Emitidas" valor={inteiro(v.emitidas)} meta="documentos no recorte" onClick={() => onDrill("recorte", "Solicitações do recorte")} />
        <Kpi rotulo="Válidas" valor={inteiro(v.validas)} meta="sem recusadas e canceladas" />
        <Kpi rotulo="Itens" valor={inteiro(v.itens)} meta="peças e itens pedidos" />
        <Kpi rotulo="Recusadas · canceladas" valor={`${inteiro(v.recusadas)} · ${inteiro(v.canceladas)}`} meta="fora de prazo e aderência" />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Emitidas × atendidas por mês" regua={v.regua} span={8}>
          <SerieMensal serie={v.serie_mensal} />
        </Card>
        <Card titulo="Por tipo" regua="documentos" span={4}>
          <Pareto linhas={(v.por_tipo || []).map((t) => ({ codigo: t.tipo, label: `${t.tipo} · ${TIPO_LABEL[t.tipo] || t.label}`, total: t.total }))}
                  vazio="Sem solicitações no período." onSelecionar={(l) => onFiltrar("tipo", l.codigo)} />
        </Card>

        <Card titulo="Quantidade pedida" regua="por unidade de medida — nunca somada entre unidades" span={4}>
          <TabelaSimples
            colunas={[{ chave: "unidade", rotulo: "Unidade" }, { chave: "total", rotulo: "Quantidade", num: true, largura: 120, render: (l) => inteiro(l.total) }]}
            linhas={v.quantidade_por_unidade} />
        </Card>
        <Card titulo="Materiais mais pedidos" regua="peças por material (nome digitado)" span={4}>
          <Pareto linhas={(v.materiais || []).map((m) => ({ codigo: m.material, label: m.material, total: m.total }))}
                  cor="var(--chart-5)" vazio="Sem materiais no período."
                  onSelecionar={(l) => onFiltrar("material", l.codigo, l.label)} />
        </Card>
        <Card titulo="Itens por documento" regua="quantos itens cada solicitação traz" span={4}>
          <div style={{ fontSize: 13 }}><Percentis p={v.itens_por_documento} /></div>
          <div className="muted tiny" style={{ marginTop: 8 }}>
            Máximo: {inteiro(v.itens_por_documento?.max)} itens num documento.
          </div>
        </Card>

        <Card titulo="Dia da semana" regua="dia de emissão" span={6}><DiaSemana dias={v.dia_semana} /></Card>
        <Card titulo="Horários de pico" regua="hora de emissão" span={6}><HorariosPico horarios={v.horarios} /></Card>

        <Card titulo="Calendário de solicitações" regua="densidade por dia de emissão" span={12}>
          <CalendarioHeatmap calendario={v.calendario} de={dados.recorte?.de} ate={dados.recorte?.ate} />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Backlog

export function AbaBacklog({ dados, onDrill, onAbrir }) {
  const b = dados.backlog;
  const vencidas = (b.estado_prazo || []).find((e) => e.estado === "VENCIDO")?.total || 0;
  const ag = b.aguardando_autorizacao || {};
  const sep = b.requisicoes_sem_separacao || {};
  return (
    <>
      <Regua>{b.regua}. Este bloco ignora o período: é o estoque de hoje.</Regua>
      <KpiStrip>
        <Kpi rotulo="Abertas agora" valor={inteiro(b.abertas)} meta={`idade p50 ${du(b.idade?.p50)}`} onClick={() => onDrill("abertas", "Abertas agora")} />
        <Kpi rotulo="Aguardando autorização" valor={inteiro(ag.total)} tone={ag.acima_do_limiar ? "warning" : undefined}
             meta={`${inteiro(ag.acima_do_limiar)} acima de ${ag.limiar_du ?? "—"} d.u.`}
             onClick={() => onDrill("aguardando_autorizacao", "Aguardando autorização")} />
        <Kpi rotulo="Vencidas" valor={inteiro(vencidas)} tone={vencidas ? "danger" : undefined} meta="prazo vigente estourado"
             onClick={() => onDrill("vencidas", "Abertas com prazo vencido")} />
        <Kpi rotulo="Prontas sem retirada" valor={inteiro(b.prontas_sem_retirada)} meta="paradas na expedição"
             onClick={() => onDrill("prontas_sem_retirada", "Prontas sem retirada")} />
        <Kpi rotulo="Peças novas sem lote" valor={inteiro(b.pecas_novas_sem_lote)}
             tone={b.pecas_sem_lote_acima_do_limiar ? "warning" : undefined}
             meta={`${inteiro(b.pecas_sem_lote_acima_do_limiar)} acima de ${b.limiar_sem_lote_du ?? "—"} d.u.`}
             onClick={() => onDrill("pecas_sem_lote", "Assistências com peça nova sem lote")} />
        <Kpi rotulo="Requisições sem separação" valor={inteiro(sep.total)}
             tone={sep.acima_do_limiar ? "warning" : undefined}
             meta={`${inteiro(sep.acima_do_limiar)} acima de ${sep.limiar_du ?? "—"} d.u.`} />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Onde estão" regua="abertas por situação" span={4}>
          <BarrasTom linhas={(b.por_situacao || []).map((s) => ({ label: SITUACAO_LABELS[s.situacao] || s.label, total: s.total, tone: SITUACAO_TONES[s.situacao] }))} vazio="Nada aberto." />
        </Card>
        <Card titulo="Idade das abertas" regua="dias úteis desde a emissão" span={4}>
          <Pareto linhas={(b.idade_faixas || []).map((f) => ({ codigo: f.faixa, label: `${f.faixa} d.u.`, total: f.total }))} vazio="Nada aberto." />
        </Card>
        <Card titulo="Situação do prazo" regua="contra o prazo vigente" span={4}>
          <BarrasTom linhas={(b.estado_prazo || []).filter((e) => e.total > 0).map((e) => ({ label: e.label, total: e.total, tone: ESTADO_PRAZO_TONE[e.estado] }))} vazio="Nada aberto." />
        </Card>
        <Card titulo="Mais antigas" regua="as 10 abertas há mais tempo — clique para abrir na Gestão" span={12}>
          <TabelaSimples
            onLinha={(l) => onAbrir(l.numero)}
            colunas={[
              { chave: "numero", rotulo: "Nº", largura: 130 },
              { chave: "tipo", rotulo: "Tipo", largura: 60 },
              { chave: "cliente", rotulo: "Cliente" },
              { chave: "solicitante", rotulo: "Solicitante", largura: "20%" },
              { chave: "situacao", rotulo: "Situação", largura: 170, render: (l) => SITUACAO_LABELS[l.situacao] || l.situacao },
              { chave: "idade_du", rotulo: "Idade", num: true, largura: 90, render: (l) => du(l.idade_du) },
            ]}
            linhas={b.mais_antigas} vazio="Nada aberto." />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Prazos

const ETAPAS = [
  ["emissao_autorizacao", "Emissão → autorização"],
  ["autorizacao_pronta", "Autorização → pronta"],
  ["pronta_expedicao", "Pronta → expedição"],
  ["autorizacao_expedicao", "Autorização → expedição"],
  ["emissao_expedicao", "Emissão → expedição"],
  ["emissao_atendimento", "Emissão → atendida"],
];

export function AbaPrazos({ dados, onDrill }) {
  const p = dados.prazos;
  const a = p.aderencia || {};
  const r = p.replanejamento || {};
  return (
    <>
      <Regua>{p.regua}.</Regua>
      <KpiStrip>
        <Kpi rotulo="Aderência honesta" valor={pct(a.pct_honesta)} meta={`${inteiro(a.no_prazo)} no prazo de ${inteiro((a.concluidos || 0) + (a.abertos_vencidos || 0))}`}
             titulo="No prazo ÷ (concluídos + abertos já vencidos), contra o prazo ORIGINAL" />
        <Kpi rotulo="Concluídos no prazo" valor={pct(a.pct_concluidos_no_prazo)} meta={`${inteiro(a.concluidos)} concluídos`} />
        <Kpi rotulo="Abertos vencidos" valor={inteiro(a.abertos_vencidos)} tone={a.abertos_vencidos ? "danger" : undefined} meta="entram no denominador" />
        <Kpi rotulo="Sem prazo" valor={inteiro(a.sem_prazo)} meta="autorizadas sem prazo" onClick={() => onDrill("sem_prazo", "Autorizadas sem prazo")} />
        <Kpi rotulo="Replanejadas" valor={pct(r.pct)} meta={`${inteiro(r.replanejados)} · ${inteiro(r.cumpriram_vigente)} cumpriram o novo prazo`} />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Tempo por etapa" regua="dias úteis; percentis (p50 = metade dos casos)" span={7}>
          <TabelaSimples
            colunas={[
              { chave: "etapa", rotulo: "Etapa" },
              { chave: "p50", rotulo: "p50", num: true, largura: 80 },
              { chave: "p80", rotulo: "p80", num: true, largura: 80 },
              { chave: "p95", rotulo: "p95", num: true, largura: 80 },
              { chave: "n", rotulo: "Casos", num: true, largura: 80 },
            ]}
            linhas={ETAPAS.map(([k, nome]) => {
              const x = p.lead_time?.[k] || {};
              return { id: k, etapa: nome, p50: du(x.p50), p80: du(x.p80), p95: du(x.p95), n: inteiro(x.n) };
            })} />
        </Card>
        <Card titulo="Atraso dos concluídos fora do prazo" regua="dias úteis além do prazo original" span={5}>
          <div style={{ fontSize: 13 }}><Percentis p={a.atraso_du} formato={du} /></div>
        </Card>
        <Card titulo="Por rota" regua="aderência e lead time (autorização → saída) por rota de prazo" span={12}>
          <TabelaSimples
            colunas={[
              { chave: "label", rotulo: "Rota" },
              { chave: "concluidos", rotulo: "Concluídos", num: true, largura: 110 },
              { chave: "no_prazo", rotulo: "No prazo", num: true, largura: 100 },
              { chave: "pct", rotulo: "% no prazo", num: true, largura: 110, render: (l) => pct(l.pct_no_prazo) },
              { chave: "abertos_vencidos", rotulo: "Abertos vencidos", num: true, largura: 140 },
              { chave: "lead", rotulo: "Lead p50 · p80", num: true, largura: 170, render: (l) => `${du(l.lead_time?.p50)} · ${du(l.lead_time?.p80)}` },
            ]}
            linhas={(p.por_rota || []).map((x) => ({ ...x, id: x.rota }))} vazio="Nenhuma solicitação com prazo no recorte." />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Qualidade

export function AbaQualidade({ dados, onDrill, onFiltrar }) {
  const q = dados.qualidade;
  const pend = q.classificacao_pendente || {};
  const causa = q.causa_pendente || {};
  const filtrar = (dim) => (l) => onFiltrar(dim, l.codigo, l.label);
  return (
    <>
      <Regua>{q.regua}.</Regua>
      <KpiStrip>
        <Kpi rotulo="Itens válidos" valor={inteiro(q.itens_validos)} meta="base dos Paretos" />
        <Kpi rotulo="Itens autorizados" valor={inteiro(q.itens_autorizados)} meta="já passaram pela triagem" />
        <Kpi rotulo="Classificação pendente" valor={inteiro(pend.total)} tone={pend.total ? "warning" : undefined}
             meta={`${pct(pend.pct)} dos autorizados`} onClick={() => onDrill("sem_classificacao", "Autorizadas com item sem classificação")} />
        <Kpi rotulo="Causa-raiz pendente" valor={inteiro(causa.total)} tone={causa.acima_do_prazo ? "warning" : undefined}
             meta={`${inteiro(causa.acima_do_prazo)} além de ${causa.prazo_du ?? "—"} d.u.`} />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Não conformidades" regua="itens por sintoma (Pareto)" span={6}><Pareto linhas={q.nc} vazio="Nenhum item classificado." onSelecionar={filtrar("nc")} /></Card>
        <Card titulo="Processo de origem" regua="itens por onde o problema nasceu" span={6}><Pareto linhas={q.processo} cor="var(--chart-3)" vazio="Nenhum item classificado." onSelecionar={filtrar("processo")} /></Card>
        <Card titulo="Causa-raiz" regua="itens por causa apurada" span={4}><Pareto linhas={q.causa} cor="var(--chart-4)" vazio="Nenhuma causa apurada." onSelecionar={filtrar("causa")} /></Card>
        <Card titulo="6M" regua="causas agrupadas nas categorias da Produção" span={4}><Pareto linhas={q.categoria_6m} cor="var(--chart-5)" vazio="Nenhuma causa apurada." /></Card>
        <Card titulo="Família da NC" regua="agrupa os sintomas" span={4}><Pareto linhas={q.familia_nc} cor="var(--chart-6)" vazio="Nenhum item classificado." /></Card>
        <Card titulo="Demanda" regua="o que se pede" span={4}><Pareto linhas={q.demanda} vazio="Nenhuma demanda informada." onSelecionar={filtrar("demanda")} /></Card>
        <Card titulo="Imputação" regua="quem arca" span={4}><Pareto linhas={q.imputacao} cor="var(--chart-1)" vazio="Sem imputação." onSelecionar={filtrar("imputacao")} /></Card>
        <Card titulo="Responsável pela NC" regua="nome digitado ou usuário" span={4}><Pareto linhas={q.responsavel} cor="var(--chart-7)" vazio="Nenhum responsável informado." /></Card>
        <Card titulo="Processo × não conformidade" regua="as combinações mais frequentes (itens)" span={7}>
          <TabelaSimples
            colunas={[
              { chave: "processo_label", rotulo: "Processo de origem" },
              { chave: "nc_label", rotulo: "Não conformidade" },
              { chave: "total", rotulo: "Itens", num: true, largura: 80 },
            ]}
            linhas={(q.processo_x_nc || []).slice(0, 15).map((x) => ({ ...x, id: `${x.processo}|${x.nc}` }))}
            vazio="Nenhum item com processo e NC." />
        </Card>
        <Card titulo="Motivo informado por quem solicitou" regua="antes da triagem — não é a classificação" span={5}>
          <MotivosDonut motivos={q.motivo_informado} />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Custos

export function AbaCustos({ dados }) {
  const c = dados.custos;
  const ranking = (linhas) => <Pareto linhas={linhas} formatar={reais} cor="var(--chart-4)" vazio="Sem estimativa no recorte." />;
  return (
    <>
      <Regua tone="warning">
        <strong>Custo ESTIMADO, não medido.</strong> {c.regua}. Quando o estoque passar a ser movimentado, o consumo real substitui esta estimativa.
      </Regua>
      <KpiStrip>
        <Kpi rotulo="Material estimado" valor={reais(c.estimado_total)} meta="estimado pela chapa" />
        <Kpi rotulo="Cobertura" valor={pct(c.cobertura_pct)} meta={`${inteiro(c.pecas_com_estimativa)} de ${inteiro(c.pecas_novas)} peças novas`}
             tone={c.cobertura_pct !== null && c.cobertura_pct < 70 ? "warning" : undefined}
             titulo="Peças novas com medida e material do cadastro com preço" />
        <Kpi rotulo="Área estimada" valor={`${Number(c.m2_estimado || 0).toLocaleString("pt-BR")} m²`} meta="peças novas com medida" />
        <Kpi rotulo="Por peça (p50)" valor={reais(c.por_peca?.p50)} meta={`p80 ${reais(c.por_peca?.p80)}`} />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Por processo de origem" regua="R$ estimado" span={6}>{ranking(c.por_processo)}</Card>
        <Card titulo="Por não conformidade" regua="R$ estimado" span={6}>{ranking(c.por_nc)}</Card>
        <Card titulo="Por imputação" regua="R$ estimado — quem arca" span={6}>{ranking(c.por_imputacao)}</Card>
        <Card titulo="Por obra" regua="R$ estimado por cliente" span={6}>{ranking(c.por_obra)}</Card>
        <Card titulo="Chapas sem custo no cadastro" regua="o que falta para estimar — corrija no Cadastro de Suprimentos" span={12}
              acao={<a href="/suprimentos/produtos" className="tiny">Abrir o cadastro</a>}>
          <TabelaSimples
            vazio={c.pecas_sem_material_cadastrado
              ? `Toda chapa usada tem custo. ${c.pecas_sem_material_cadastrado} peça(s) sem chapa do cadastro vinculada.`
              : "Toda chapa usada tem custo no cadastro."}
            colunas={[
              { chave: "codigo", rotulo: "Código", largura: 160 },
              { chave: "nome", rotulo: "Chapa" },
              { chave: "pecas", rotulo: "Peças sem estimativa", num: true, largura: 170 },
            ]}
            linhas={(c.materiais_sem_custo || []).map((m) => ({ ...m, id: m.produto_id }))} />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Reincidência

export function AbaReincidencia({ dados, onFiltrar }) {
  const r = dados.reincidencia;
  return (
    <>
      <Regua>{r.regua}.</Regua>
      <KpiStrip>
        <Kpi rotulo="Obras" valor={inteiro(r.obras)} meta="clientes com solicitação" />
        <Kpi rotulo="Concentração" valor={inteiro(r.obras_para_80pct_itens)} meta="obras somam 80% dos itens" />
        <Kpi rotulo="Obras com muitas ondas" valor={inteiro(r.com_ondas_alerta)} tone={r.com_ondas_alerta ? "warning" : undefined}
             meta={`${r.ondas_alerta ?? "—"} ou mais dias com pedido`} />
        <Kpi rotulo="Entre ondas (p50)" valor={du(r.intervalo_entre_ondas_du?.p50)} meta="dias úteis entre pedidos da mesma obra" />
      </KpiStrip>
      <div className="widget-grid">
        <Card titulo="Obras que mais solicitam" regua="documentos, itens e ondas (dias distintos de emissão)" span={12}>
          <TabelaSimples
            onLinha={(l) => onFiltrar("cliente", l.cliente_id || l.cliente, l.cliente)}
            colunas={[
              { chave: "cliente", rotulo: "Cliente", render: (l) => (l.digitado ? `${l.cliente} (digitado)` : l.cliente) },
              { chave: "documentos", rotulo: "Documentos", num: true, largura: 120 },
              { chave: "itens", rotulo: "Itens", num: true, largura: 90 },
              { chave: "ondas", rotulo: "Ondas", num: true, largura: 90 },
            ]}
            linhas={(r.ranking || []).map((x, i) => ({ ...x, id: x.cliente_id || `${x.cliente}-${i}` }))} />
        </Card>
        <Card titulo="Pedidos com 2 ou mais solicitações" regua="número de pedido digitado" span={5}>
          <TabelaSimples
            onLinha={(l) => onFiltrar("num_pedido", l.num_pedido)}
            colunas={[{ chave: "num_pedido", rotulo: "Pedido" }, { chave: "documentos", rotulo: "Documentos", num: true, largura: 120 }]}
            linhas={(r.pedidos_com_2_ou_mais || []).map((x) => ({ ...x, id: x.num_pedido }))} vazio="Nenhum pedido repetido." />
        </Card>
        <Card titulo="Ambientes recorrentes" regua="mesmo ambiente da mesma obra com 2+ itens" span={7}>
          <TabelaSimples
            colunas={[{ chave: "cliente", rotulo: "Cliente" }, { chave: "ambiente", rotulo: "Ambiente" }, { chave: "itens", rotulo: "Itens", num: true, largura: 80 }]}
            linhas={(r.ambientes_recorrentes || []).map((x, i) => ({ ...x, id: `${x.cliente}-${x.ambiente}-${i}` }))} vazio="Nenhum ambiente recorrente." />
        </Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Pessoas

const COLUNAS_PESSOA = [
  { chave: "nome", rotulo: "Nome" },
  { chave: "documentos", rotulo: "Docs", num: true, largura: 70 },
  { chave: "itens", rotulo: "Itens", num: true, largura: 70 },
  { chave: "pct_itens", rotulo: "% itens", num: true, largura: 80, render: (l) => pct(l.pct_itens) },
];

export function AbaPessoas({ dados }) {
  const p = dados.pessoas;
  const comId = (l) => (l || []).map((x) => ({ ...x, id: x.nome }));
  return (
    <>
      <Regua>{p.regua}. Volume não é culpa: quem registra mais aparece mais.</Regua>
      <div className="widget-grid">
        <Card titulo="Quem registrou" regua="usuário que criou a solicitação" span={4}><TabelaSimples colunas={COLUNAS_PESSOA} linhas={comId(p.solicitantes)} /></Card>
        <Card titulo="Líder de montagem" regua="nome digitado" span={4}><TabelaSimples colunas={COLUNAS_PESSOA} linhas={comId(p.lideres)} vazio="Nenhum líder informado." /></Card>
        <Card titulo="Supervisor" regua="nome digitado" span={4}><TabelaSimples colunas={COLUNAS_PESSOA} linhas={comId(p.supervisores)} vazio="Nenhum supervisor informado." /></Card>
        <Card titulo="Quem autoriza" regua="tempo de triagem (emissão → autorização) em dias úteis" span={7}>
          <TabelaSimples
            colunas={[
              { chave: "nome", rotulo: "Nome" },
              { chave: "documentos", rotulo: "Docs", num: true, largura: 70 },
              { chave: "p50", rotulo: "p50", num: true, largura: 90, render: (l) => du(l.triagem_du?.p50) },
              { chave: "p80", rotulo: "p80", num: true, largura: 90, render: (l) => du(l.triagem_du?.p80) },
            ]}
            linhas={comId(p.autorizadores)} vazio="Nenhuma autorização no recorte." />
        </Card>
        <Card titulo="Setor (ASP)" regua="assistências à produção por setor" span={5}><Pareto linhas={p.setor_asp} vazio="Nenhuma ASP no recorte." /></Card>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ Lotes e dados

const CAMPOS_DADOS = [
  ["sem_cliente_cadastrado_pct", "Sem cliente do cadastro (nome digitado)"],
  ["sem_pedido_pct", "Sem número de pedido"],
  ["sem_lider_pct", "Sem líder de montagem (ASM e AST)"],
  ["sem_prazo_pct", "Autorizadas sem prazo"],
  ["pecas_sem_material_cadastrado_pct", "Peças sem material do cadastro"],
  ["itens_requisicao_sem_produto_pct", "Itens de requisição sem produto do cadastro"],
  ["motivo_outro_pct", "Motivo \"Outro\""],
];

export function AbaDados({ dados }) {
  const d = dados.dados;
  const l = dados.lotes;
  const cal = dados.recorte?.calendario || {};
  return (
    <div className="widget-grid">
      <Card titulo="Qualidade do dado" regua={d.regua} span={7}>
        <div className="stack" style={{ gap: 10 }}>
          {CAMPOS_DADOS.map(([chave, rotulo]) => {
            const v = d[chave];
            return (
              <div key={chave}>
                <div className="flex between" style={{ fontSize: 12.5, marginBottom: 3 }}>
                  <span className="ell">{rotulo}</span><strong className="mono">{pct(v)}</strong>
                </div>
                <div style={{ height: 7, borderRadius: 4, background: "var(--surface-2)", overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, Number(v) || 0)}%`, height: "100%", borderRadius: 4, background: Number(v) >= 50 ? "var(--warning-solid)" : "var(--chart-2)" }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      <Card titulo="Lotes de corte" regua={l.regua} span={5}>
        <TabelaSimples
          colunas={[{ chave: "k", rotulo: "Medida" }, { chave: "v", rotulo: "Valor", num: true, largura: 170 }]}
          linhas={[
            { id: 1, k: "Peças novas", v: inteiro(l.pecas_novas) },
            { id: 2, k: "Em lote", v: inteiro(l.em_lote) },
            { id: 3, k: "Sem lote", v: inteiro(l.sem_lote) },
            { id: 4, k: "Lotes", v: inteiro(l.lotes) },
            { id: 5, k: "Peças por lote (p50 · p80)", v: `${inteiro(l.pecas_por_lote?.p50)} · ${inteiro(l.pecas_por_lote?.p80)}` },
            { id: 6, k: "Só mão de obra", v: inteiro(l.mao_de_obra) },
          ]} />
      </Card>
      <Card titulo="Calendário usado nas contas" regua="dias úteis e feriados vêm do calendário da unidade (Configurações > Calendário)" span={12}>
        <div style={{ fontSize: 13 }}>
          Dias úteis:{" "}
          <strong>
            {(Array.isArray(cal.dias_uteis) ? cal.dias_uteis : []).map((d) => DIAS_SEMANA[d] || d).join(", ") || "—"}
          </strong>.{" "}
          Feriados no período: <strong>{(cal.feriados_no_periodo || []).length || "nenhum cadastrado"}</strong>.
        </div>
        {(cal.feriados_no_periodo || []).length === 0 && (
          <div className="muted tiny" style={{ marginTop: 6 }}>
            Sem feriados cadastrados, os dias úteis contam feriado como dia de trabalho — prazos e idades podem sair um pouco otimistas.
          </div>
        )}
      </Card>
    </div>
  );
}
